import React, { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, Reorder, useDragControls } from "framer-motion";
import { LayoutTemplate, X, Plus, Trash2, User, Users, GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { templatesApi, templateAttachmentsApi } from "../../lib/api";
import { confirmDialog } from "../../lib/confirm";
import { kirliMi, KIRLI_UYARI } from "../../lib/formDirty";
import { useFormDraft } from "../../lib/useFormDraft";
import { DraftRestoreBar } from "../DraftRestoreBar";
import { REMINDER_DAY_CHOICES } from "../../lib/taskHelpers";
import { flattenCategoryOptions } from "../../lib/categoryTree";
import { TaskAttachments } from "./TaskAttachments";
import useDragAutoScroll from "../../lib/useDragAutoScroll";

// Tek alt görev satırı: sıra no + sürükleme tutamacı + yerinde ad düzenleme +
// yukarı/aşağı yedek denetimleri + sil. Görev kartındaki (SubtaskRow) mekanizmanın
// aynısı: framer-motion Reorder + useDragControls.
const TemplateSubtaskRow = ({ sub, index, total, onRename, onRemove, onMove, onDragActive }) => {
  const controls = useDragControls();
  const [value, setValue] = useState(sub.text);
  const [warn, setWarn] = useState(false);
  const lastGood = useRef(sub.text);

  useEffect(() => {
    setValue(sub.text);
    lastGood.current = sub.text;
  }, [sub.text]);

  const commit = () => {
    const t = value.trim();
    if (!t) {
      setValue(lastGood.current);
      setWarn(true);
      return;
    }
    setWarn(false);
    lastGood.current = t;
    if (t !== sub.text) onRename(sub.id, t);
  };

  return (
    <Reorder.Item
      value={sub}
      dragListener={false}
      dragControls={controls}
      layout
      data-testid={`template-subtask-${index}`}
      className="rounded border border-sertex-cyan/15 bg-sertex-surface/40 px-1.5 py-1"
    >
      <div className="flex items-center gap-1.5 group/tsub">
        <button
          type="button"
          onPointerDown={(e) => { e.preventDefault(); onDragActive(e); controls.start(e); }}
          data-testid={`template-subtask-drag-${index}`}
          title="Sürükle sırala"
          aria-label="Sürükle sırala"
          className="shrink-0 h-5 w-3 flex items-center justify-center text-sertex-cyan/60 hover:text-sertex-cyan cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-3 w-3" />
        </button>
        <span
          data-testid={`template-subtask-number-${index}`}
          className="shrink-0 tabular-nums font-semibold font-mono text-[12px] text-sertex-cyan w-6 text-right"
        >
          {index + 1}.
        </span>
        <input
          value={value}
          onChange={(e) => { setValue(e.target.value); if (warn) setWarn(false); }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); e.currentTarget.blur(); }
            else if (e.key === "Escape") { e.preventDefault(); setValue(lastGood.current); setWarn(false); e.currentTarget.blur(); }
            else if (e.altKey && e.key === "ArrowUp") { e.preventDefault(); onMove(index, -1); }
            else if (e.altKey && e.key === "ArrowDown") { e.preventDefault(); onMove(index, 1); }
          }}
          data-testid={`template-subtask-text-${index}`}
          title="Adı düzenlemek için yazın (Enter: kaydet · Esc: geri al)"
          className={`flex-1 min-w-0 bg-transparent border-b text-[12px] font-mono text-sertex-text outline-none pb-0.5 ${warn ? "border-rose-400" : "border-transparent hover:border-sertex-cyan/30 focus:border-sertex-cyan"}`}
        />
        <div className="shrink-0 flex flex-col opacity-40 group-hover/tsub:opacity-100 focus-within:opacity-100 transition-opacity">
          <button type="button" onClick={() => onMove(index, -1)} disabled={index === 0}
            data-testid={`template-subtask-up-${index}`} title="Yukarı taşı" aria-label="Yukarı taşı"
            className="h-3 w-4 flex items-center justify-center text-sertex-textMuted hover:text-sertex-cyan disabled:opacity-20">
            <ChevronUp className="h-3 w-3" />
          </button>
          <button type="button" onClick={() => onMove(index, 1)} disabled={index === total - 1}
            data-testid={`template-subtask-down-${index}`} title="Aşağı taşı" aria-label="Aşağı taşı"
            className="h-3 w-4 flex items-center justify-center text-sertex-textMuted hover:text-sertex-cyan disabled:opacity-20">
            <ChevronDown className="h-3 w-3" />
          </button>
        </div>
        <button type="button" onClick={() => onRemove(sub.id)} data-testid={`template-subtask-remove-${index}`}
          title="Sil" aria-label="Sil"
          className="shrink-0 p-1 text-sertex-textMuted hover:text-rose-400">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      {warn && (
        <div className="hud-text text-rose-300 pl-8" data-testid={`template-subtask-warn-${index}`}>
          Alt görev adı boş olamaz
        </div>
      )}
    </Reorder.Item>
  );
};

// Şablon oluştur / düzenle. Yeni şablonda "Kaydet" önce şablonu oluşturur,
// ardından dosya ekleri bölümü açılır (ekler bir şablon id'si gerektirir).
export const TemplateFormModal = ({ template, categories = [], currentUser = null, onClose, onSaved, taslakOtoYukle = false, onTaslakGeriYukle }) => {
  const [tplId, setTplId] = useState(template?.id || null);
  const [name, setName] = useState(template?.name || "");
  const [title, setTitle] = useState(template?.title || "");
  const [description, setDescription] = useState(template?.description || "");
  const [categoryId, setCategoryId] = useState(template?.category_id || "");
  const [reminderDays, setReminderDays] = useState(
    template?.reminder_disabled ? "__off__" : (template?.reminder_days == null ? "" : String(template.reminder_days)),
  );
  const [scope, setScope] = useState(template?.scope === "shared" ? "shared" : "personal");
  const [subtasks, setSubtasks] = useState(template?.subtasks || []);
  const [newSub, setNewSub] = useState("");
  const [saving, setSaving] = useState(false);

  // FAZ F — kaydetmeden çıkışta uyarı (ortak formDirty yardımcısı).
  const anlikRef = useRef(null);
  anlikRef.current = { name, title, description, categoryId, reminderDays, scope, subtasks, newSub };
  const baslangicRef = useRef(null);
  if (baslangicRef.current === null) baslangicRef.current = anlikRef.current;
  const tryClose = async () => {
    if (kirliMi(anlikRef.current, baslangicRef.current)) {
      const ok = await confirmDialog(KIRLI_UYARI);
      if (!ok) return;
      if (taslakYaz()) {
        if (onTaslakGeriYukle) {
          toast("Taslak kaydedildi", {
            action: { label: "GERİ YÜKLE", onClick: () => onTaslakGeriYukle(template?.id || null) },
            duration: 8000,
          });
        } else {
          toast("Taslak kaydedildi", { description: "Formu tekrar açtığınızda geri yükleyebilirsiniz", duration: 8000 });
        }
      }
    }
    onClose();
  };

  // FAZ G — taslak ŞABLON BAŞINA anahtarlanır (yeni şablon için ayrı anahtar).
  const { teklif, setTeklif, yaz: taslakYaz, sil: taslakSilBu } = useFormDraft({
    userId: currentUser?.id,
    formKey: template?.id ? `template-edit:${template.id}` : "template-new",
    topla: () => ({ alanlar: anlikRef.current }),
    kirli: () => kirliMi(anlikRef.current, baslangicRef.current),
  });

  const geriYukle = (veri) => {
    const a = veri?.alanlar || {};
    setName(a.name ?? "");
    setTitle(a.title ?? "");
    setDescription(a.description ?? "");
    setCategoryId(a.categoryId ?? "");
    setReminderDays(a.reminderDays ?? "");
    setScope(a.scope === "shared" ? "shared" : "personal");
    setSubtasks(Array.isArray(a.subtasks) ? a.subtasks : []);
    setNewSub(a.newSub ?? "");
    setTeklif(null);
    toast.success("Taslak geri yüklendi");
  };

  useEffect(() => {
    if (taslakOtoYukle && teklif?.veri) geriYukle(teklif.veri);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taslakOtoYukle, teklif]);
  const scrollRef = useRef(null);
  // AŞAMA 7 — ortak kenar otomatik kaydırma kancası. Davranış aynı kaldı
  // (kenar bölgesi + yumuşak hız eğrisi); kod tek yerde toplandı.
  const modalAutoScroll = useDragAutoScroll();
  const setDragActive = useCallback((ev) => {
    if (ev) modalAutoScroll.start(ev, scrollRef.current);
    else modalAutoScroll.stop();
  }, [modalAutoScroll]);

  const addSub = () => {
    const t = newSub.trim();
    if (!t) return;
    setSubtasks((prev) => [...prev, { id: `tmp-${Date.now()}-${prev.length}`, text: t }]);
    setNewSub("");
  };

  const renameSub = useCallback((id, text) => {
    setSubtasks((prev) => prev.map((s) => (s.id === id ? { ...s, text } : s)));
  }, []);
  const removeSub = useCallback((id) => {
    setSubtasks((prev) => prev.filter((s) => s.id !== id));
  }, []);
  const moveSub = useCallback((index, delta) => {
    setSubtasks((prev) => {
      const to = index + delta;
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [it] = next.splice(index, 1);
      next.splice(to, 0, it);
      return next;
    });
  }, []);

  // Sürükleme sırasında listenin üst/alt kenarına yaklaşınca modalı kaydır
  // (uzun listelerde — 14+ alt görev — sürükleme aksi halde kullanılamaz).
  // AŞAMA 7: ortak `useDragAutoScroll` kancasına taşındı (yukarıda).

  const buildBody = () => {
    const body = {
      name: name.trim(),
      title: title.trim(),
      description: description.trim(),
      category_id: categoryId || null,
      scope,
      subtasks: subtasks.filter((s) => (s.text || "").trim()).map((s) => ({ text: s.text.trim() })),
    };
    if (reminderDays === "__off__") {
      body.reminder_disabled = true;
      body.reminder_days = 0;
    } else if (reminderDays === "") {
      body.reminder_disabled = false;
      body.reminder_days = null;
    } else {
      body.reminder_disabled = false;
      body.reminder_days = parseInt(reminderDays, 10);
    }
    return body;
  };

  const handleSave = async () => {
    if (name.trim().length < 2) {
      toast.error("Şablon adı en az 2 karakter olmalı");
      return;
    }
    setSaving(true);
    try {
      const body = buildBody();
      if (tplId) {
        await templatesApi.update(tplId, body);
        taslakSilBu();
        toast.success("Şablon güncellendi");
        onSaved?.();
        onClose();
      } else {
        const created = await templatesApi.create(body);
        taslakSilBu();
        setTplId(created.id);
        toast.success("Şablon kaydedildi — artık dosya ekleyebilirsiniz");
        onSaved?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[130] bg-sertex-bg/70 backdrop-blur-sm" onClick={tryClose} data-testid="template-form-backdrop" />
      <div className="fixed inset-0 z-[140] flex items-center justify-center pointer-events-none px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          ref={scrollRef}
          className="pointer-events-auto w-full max-w-[460px] max-h-[88vh] overflow-y-auto glass-panel corner-bracket p-4 space-y-3"
          data-testid="template-form-modal"
        >
          <div className="flex items-center justify-between border-b border-sertex-cyan/20 pb-2">
            <div className="display-text text-sertex-cyan neon-glow tracking-[0.2em] flex items-center gap-2">
              <LayoutTemplate className="h-4 w-4" /> {tplId ? "ŞABLONU DÜZENLE" : "YENİ ŞABLON"}
            </div>
            <button onClick={tryClose} data-testid="template-form-close" className="p-1 hover:bg-sertex-cyan/10 rounded text-sertex-textMuted hover:text-sertex-cyan">
              <X className="h-4 w-4" />
            </button>
          </div>

          {teklif && !taslakOtoYukle && (
            <DraftRestoreBar
              at={teklif.at}
              onRestore={() => geriYukle(teklif.veri)}
              onIgnore={() => { taslakSilBu(); toast("Taslak yoksayıldı"); }}
              testid="template-draft-bar"
            />
          )}

          <div>
            <div className="hud-text text-sertex-textMuted mb-1">ŞABLON ADI *</div>
            <input value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="template-name"
              placeholder="Örn: Haftalık Rapor"
              className="w-full bg-sertex-surface/60 border border-sertex-cyan/25 rounded-md px-2 py-1.5 text-sm font-mono text-sertex-text placeholder:text-sertex-textMuted focus:border-sertex-cyan outline-none" />
          </div>

          {/* Kapsam — Kişisel / Ekip ortak */}
          <div>
            <div className="hud-text text-sertex-textMuted mb-1">KAPSAM</div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setScope("personal")} data-testid="template-scope-personal"
                className={`flex items-center justify-center gap-1.5 py-1.5 rounded-md border hud-text transition-colors ${scope === "personal" ? "border-sertex-cyan text-sertex-cyan bg-sertex-cyan/10" : "border-sertex-textMuted/30 text-sertex-textMuted hover:border-sertex-cyan/50"}`}>
                <User className="h-3.5 w-3.5" /> Kişisel
              </button>
              <button type="button" onClick={() => setScope("shared")} data-testid="template-scope-shared"
                className={`flex items-center justify-center gap-1.5 py-1.5 rounded-md border hud-text transition-colors ${scope === "shared" ? "border-sertex-cyan text-sertex-cyan bg-sertex-cyan/10" : "border-sertex-textMuted/30 text-sertex-textMuted hover:border-sertex-cyan/50"}`}>
                <Users className="h-3.5 w-3.5" /> Ekip (şirket)
              </button>
            </div>
          </div>

          <div>
            <div className="hud-text text-sertex-textMuted mb-1">GÖREV BAŞLIĞI</div>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="template-title"
              placeholder="Görev oluşturulunca gelecek başlık"
              className="w-full bg-sertex-surface/60 border border-sertex-cyan/25 rounded-md px-2 py-1.5 text-sm font-mono text-sertex-text placeholder:text-sertex-textMuted focus:border-sertex-cyan outline-none" />
          </div>

          <div>
            <div className="hud-text text-sertex-textMuted mb-1">AÇIKLAMA</div>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} data-testid="template-description"
              className="w-full bg-sertex-surface/60 border border-sertex-cyan/25 rounded-md px-2 py-1.5 text-sm font-mono text-sertex-text placeholder:text-sertex-textMuted focus:border-sertex-cyan outline-none resize-none" />
          </div>

          {categories.length > 0 && (
            <div>
              <div className="hud-text text-sertex-textMuted mb-1">🏷️ İŞ KOLU</div>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} data-testid="template-category"
                className="w-full bg-sertex-surface/60 border border-sertex-cyan/25 rounded-md px-2 py-1.5 text-sm font-mono text-sertex-text focus:border-sertex-cyan outline-none">
                <option value="">— İş kolu yok —</option>
                {flattenCategoryOptions(categories).map((o) => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <div className="hud-text text-sertex-textMuted mb-1">⏱ YAKLAŞAN UYARISI</div>
            <select value={reminderDays} onChange={(e) => setReminderDays(e.target.value)} data-testid="template-reminder-days"
              className="w-full bg-sertex-surface/60 border border-sertex-cyan/25 rounded-md px-2 py-1.5 text-sm font-mono text-sertex-text focus:border-sertex-cyan outline-none">
              <option value="">Varsayılan (hiyerarşi)</option>
              {REMINDER_DAY_CHOICES.map((d) => (<option key={d} value={d}>{d} gün önce</option>))}
              <option value="__off__">🚫 Kapalı</option>
            </select>
          </div>

          {/* Alt görevler */}
          <div>
            <div className="hud-text text-sertex-textMuted mb-1">ALT GÖREVLER</div>
            <Reorder.Group axis="y" values={subtasks} onReorder={setSubtasks} className="space-y-1" data-testid="template-subtask-list">
              {subtasks.map((s, i) => (
                <TemplateSubtaskRow
                  key={s.id || i}
                  sub={s}
                  index={i}
                  total={subtasks.length}
                  onRename={renameSub}
                  onRemove={removeSub}
                  onMove={moveSub}
                  onDragActive={setDragActive}
                />
              ))}
            </Reorder.Group>
            <div className="flex gap-2 mt-1">
              <input value={newSub} onChange={(e) => setNewSub(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSub())}
                data-testid="template-subtask-input" placeholder="Alt görev ekle…"
                className="flex-1 bg-sertex-surface/60 border border-sertex-cyan/25 rounded-md px-2 py-1.5 text-sm font-mono text-sertex-text placeholder:text-sertex-textMuted focus:border-sertex-cyan outline-none" />
              <button onClick={addSub} data-testid="template-subtask-add" className="px-2 rounded-md border border-sertex-cyan/30 text-sertex-cyan hover:bg-sertex-cyan/10">
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Dosya ekleri — yalnızca şablon kaydedildikten sonra */}
          {tplId ? (
            <div className="border-t border-sertex-cyan/15 pt-1">
              <TaskAttachments taskId={tplId} currentUserId={currentUser?.id} canManage attachmentApi={templateAttachmentsApi} />
            </div>
          ) : (
            <div className="hud-text text-sertex-textMuted/70 border-t border-sertex-cyan/15 pt-2">
              📎 Dosya eklemek için önce şablonu kaydedin.
            </div>
          )}

          <div className="flex gap-2 pt-2 border-t border-sertex-cyan/15">
            <button onClick={tryClose} data-testid="template-form-cancel" className="flex-1 py-2 border border-sertex-cyan/25 text-sertex-textMuted hover:text-sertex-cyan hover:border-sertex-cyan/50 rounded-md hud-text transition-colors">
              {tplId ? "KAPAT" : "İPTAL"}
            </button>
            <button onClick={handleSave} disabled={saving} data-testid="template-save"
              className="flex-1 py-2 bg-sertex-cyan/20 border border-sertex-cyan text-sertex-cyan hover:bg-sertex-cyan hover:text-sertex-bg rounded-md hud-text transition-colors disabled:opacity-40">
              {tplId ? "GÜNCELLE" : "KAYDET"}
            </button>
          </div>
        </motion.div>
      </div>
    </>,
    document.body,
  );
};

export default TemplateFormModal;
